import React, { useRef, useEffect } from "react";
import { setupCanvas } from "./canvasUtils";

// ============================================
// Chapter 18  -  Data Analysis Basics
// ============================================

/** 18.1 – Histogram and Binning */
export const HistogramBinning = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Histograms and Binning", W / 2, 24);

    const m = { left: 60, right: 30, top: 48, bottom: 50 };
    const pw = W - m.left - m.right,
      ph = H - m.top - m.bottom;

    // Axes
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(m.left, m.top);
    ctx.lineTo(m.left, m.top + ph);
    ctx.lineTo(m.left + pw, m.top + ph);
    ctx.stroke();

    // Gaussian histogram
    const nBins = 20;
    const binW = pw / nBins;
    const maxCount = 45;
    const mu = 10,
      sigma = 3;

    for (let b = 0; b < nBins; b++) {
      const xCenter = b + 0.5;
      const gauss =
        maxCount *
        Math.exp(-0.5 * Math.pow((xCenter - mu) / sigma, 2));
      const count = Math.round(gauss + (Math.random() - 0.5) * 3);
      const barH = (Math.max(count, 0) / maxCount) * ph;
      const x = m.left + b * binW;

      ctx.fillStyle = "#3B82F6" + "50";
      ctx.fillRect(x, m.top + ph - barH, binW - 1, barH);
      ctx.strokeStyle = "#3B82F6";
      ctx.lineWidth = 1;
      ctx.strokeRect(x, m.top + ph - barH, binW - 1, barH);
    }

    // Gaussian curve overlay
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let px = 0; px <= pw; px++) {
      const xVal = (px / pw) * nBins;
      const gauss =
        maxCount *
        Math.exp(-0.5 * Math.pow((xVal - mu) / sigma, 2));
      const y = m.top + ph - (gauss / maxCount) * ph;
      if (px === 0) ctx.moveTo(m.left + px, y);
      else ctx.lineTo(m.left + px, y);
    }
    ctx.stroke();

    // Axis labels
    ctx.save();
    ctx.translate(16, m.top + ph / 2);
    ctx.rotate(-Math.PI / 2);
    ctx.fillStyle = "#475569";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Counts / bin", 0, 0);
    ctx.restore();
    ctx.fillStyle = "#475569";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Measured value (e.g., TOF in ns)", m.left + pw / 2, H - 8);

    // Binning annotations
    ctx.strokeStyle = "#F59E0B";
    ctx.lineWidth = 1.5;
    const arrowBin = 5;
    const ax = m.left + arrowBin * binW;
    ctx.beginPath();
    ctx.moveTo(ax, m.top + ph + 5);
    ctx.lineTo(ax, m.top + ph + 18);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(ax + binW, m.top + ph + 5);
    ctx.lineTo(ax + binW, m.top + ph + 18);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(ax, m.top + ph + 14);
    ctx.lineTo(ax + binW, m.top + ph + 14);
    ctx.stroke();
    ctx.fillStyle = "#F59E0B";
    ctx.font = "bold 9px Inter,sans-serif";
    ctx.fillText("bin width", ax + binW / 2, m.top + ph + 30);

    // Legend
    ctx.fillStyle = "#3B82F6";
    ctx.fillRect(400, 46, 14, 10);
    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Data histogram", 420, 55);
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(400, 68);
    ctx.lineTo(414, 68);
    ctx.stroke();
    ctx.fillStyle = "#475569";
    ctx.fillText("Gaussian fit", 420, 72);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-slate-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 18.2 – Fitting Functions */
export const FittingFunctions = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Common Fitting Functions", W / 2, 24);

    // Three mini-plots side by side
    const plots = [
      {
        title: "Gaussian",
        eq: "A·exp(-(x-μ)²/2σ²)",
        color: "#3B82F6",
        fn: (x) => Math.exp(-0.5 * Math.pow((x - 0.5) / 0.15, 2)),
      },
      {
        title: "Exponential",
        eq: "A·exp(-x/τ)",
        color: "#22C55E",
        fn: (x) => Math.exp(-x * 4),
      },
      {
        title: "Polynomial",
        eq: "a₀ + a₁x + a₂x²",
        color: "#F59E0B",
        fn: (x) => 0.8 - 2.5 * (x - 0.5) * (x - 0.5) + 0.2,
      },
    ];

    const pw2 = 135,
      ph2 = 100;
    plots.forEach((p, i) => {
      const ox = 30 + i * 175,
        oy = 50;

      // Mini axes
      ctx.strokeStyle = "#94A3B8";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(ox, oy);
      ctx.lineTo(ox, oy + ph2);
      ctx.lineTo(ox + pw2, oy + ph2);
      ctx.stroke();

      // Function curve
      ctx.strokeStyle = p.color;
      ctx.lineWidth = 2.5;
      ctx.beginPath();
      for (let px = 0; px <= pw2; px++) {
        const xVal = px / pw2;
        const yVal = p.fn(xVal);
        const y = oy + ph2 - yVal * ph2;
        if (px === 0) ctx.moveTo(ox + px, y);
        else ctx.lineTo(ox + px, y);
      }
      ctx.stroke();

      // Scatter data points on curve
      ctx.fillStyle = p.color;
      for (let j = 0; j < 8; j++) {
        const xVal = 0.05 + j * 0.12 + Math.random() * 0.05;
        const yVal = p.fn(xVal) + (Math.random() - 0.5) * 0.12;
        const dx = ox + xVal * pw2;
        const dyy = oy + ph2 - Math.max(0, Math.min(1, yVal)) * ph2;
        ctx.beginPath();
        ctx.arc(dx, dyy, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Title & equation
      ctx.fillStyle = p.color;
      ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(p.title, ox + pw2 / 2, oy + ph2 + 20);
      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      ctx.fillText(p.eq, ox + pw2 / 2, oy + ph2 + 34);
    });

    // Parameters info
    ctx.fillStyle = "#EFF6FF";
    ctx.beginPath();
    ctx.roundRect(30, H - 55, 480, 42, 8);
    ctx.fill();
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Fitting = finding parameters that minimize residuals between model & data",
      W / 2,
      H - 35
    );
    ctx.fillStyle = "#64748B";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText(
      "Use χ² or maximum likelihood methods  -  choose function based on physics",
      W / 2,
      H - 18
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 18.3 – Chi-Squared Fit Goodness */
export const ChiSquaredFit = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Chi-Squared (χ²)  -  Goodness of Fit", W / 2, 24);

    // Formula
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 16px Inter,sans-serif";
    ctx.fillText("χ² = Σᵢ (yᵢ − f(xᵢ))² / σᵢ²", W / 2, 55);

    // Visual: data vs fit with residuals
    const m = { left: 60, right: 220, top: 80, bottom: 40 };
    const pw = W - m.left - m.right,
      ph = H - m.top - m.bottom;

    // Axes
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(m.left, m.top);
    ctx.lineTo(m.left, m.top + ph);
    ctx.lineTo(m.left + pw, m.top + ph);
    ctx.stroke();

    // Fit line
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(m.left, m.top + ph * 0.8);
    ctx.lineTo(m.left + pw, m.top + ph * 0.2);
    ctx.stroke();

    // Data points with error bars and residuals
    const pts = [
      { x: 0.1, y: 0.25, e: 0.08 },
      { x: 0.25, y: 0.4, e: 0.06 },
      { x: 0.4, y: 0.42, e: 0.07 },
      { x: 0.55, y: 0.55, e: 0.05 },
      { x: 0.7, y: 0.6, e: 0.06 },
      { x: 0.85, y: 0.78, e: 0.07 },
      { x: 0.95, y: 0.82, e: 0.05 },
    ];
    pts.forEach((p) => {
      const px = m.left + p.x * pw;
      const py = m.top + ph - p.y * ph;
      const fitY = m.top + ph * 0.8 - p.x * ph * 0.6;

      // Residual line
      ctx.strokeStyle = "#EF444480";
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.beginPath();
      ctx.moveTo(px, py);
      ctx.lineTo(px, fitY);
      ctx.stroke();
      ctx.setLineDash([]);

      // Error bar
      ctx.strokeStyle = "#475569";
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(px, py - p.e * ph);
      ctx.lineTo(px, py + p.e * ph);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(px - 3, py - p.e * ph);
      ctx.lineTo(px + 3, py - p.e * ph);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(px - 3, py + p.e * ph);
      ctx.lineTo(px + 3, py + p.e * ph);
      ctx.stroke();

      // Point
      ctx.beginPath();
      ctx.arc(px, py, 4, 0, Math.PI * 2);
      ctx.fillStyle = "#1E293B";
      ctx.fill();
    });

    // Interpretation guide (right side)
    const rx = 340;
    const interp = [
      {
        range: "χ²/NDF ≈ 1",
        desc: "Good fit  -  model\nmatches data well",
        color: "#22C55E",
      },
      {
        range: "χ²/NDF >> 1",
        desc: "Bad fit  -  model\nmisses the data",
        color: "#EF4444",
      },
      {
        range: "χ²/NDF << 1",
        desc: "Over-fitted or\nerrors overestimated",
        color: "#F59E0B",
      },
    ];
    interp.forEach((item, i) => {
      const y = 80 + i * 56;
      ctx.fillStyle = item.color + "10";
      ctx.beginPath();
      ctx.roundRect(rx, y, 180, 48, 6);
      ctx.fill();
      ctx.strokeStyle = item.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(rx, y, 180, 48, 6);
      ctx.stroke();
      ctx.fillStyle = item.color;
      ctx.font = "bold 11px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(item.range, rx + 8, y + 16);
      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      item.desc
        .split("\n")
        .forEach((l, li) => ctx.fillText(l, rx + 8, y + 30 + li * 12));
    });
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 18.4 – Error Bars and Uncertainties */
export const ErrorBars = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Error Bars and Uncertainties", W / 2, 24);

    // Types of errors
    const types = [
      {
        title: "Statistical",
        symbol: "σ_stat = √N",
        desc: "From limited number\nof measurements.\nDecreases with more data.",
        color: "#3B82F6",
        icon: "📊",
      },
      {
        title: "Systematic",
        symbol: "σ_sys (estimated)",
        desc: "From calibration, alignment,\nmethod bias.\nDoes NOT decrease with N.",
        color: "#EF4444",
        icon: "⚙️",
      },
      {
        title: "Combined",
        symbol: "σ_tot = √(σ²_stat + σ²_sys)",
        desc: "Total uncertainty:\nadd in quadrature\n(independent errors).",
        color: "#8B5CF6",
        icon: "Σ",
      },
    ];

    types.forEach((t, i) => {
      const x = 15 + i * 178,
        y = 48,
        w = 168,
        h = 110;
      ctx.fillStyle = t.color + "08";
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.fill();
      ctx.strokeStyle = t.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.stroke();

      ctx.fillStyle = t.color;
      ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(t.title, x + w / 2, y + 18);
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.fillText(t.symbol, x + w / 2, y + 34);

      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      t.desc
        .split("\n")
        .forEach((l, li) => ctx.fillText(l, x + w / 2, y + 52 + li * 13));
    });

    // Visual example: a data point with error bars
    const ey = 200;
    ctx.fillStyle = "#1E293B";
    ctx.beginPath();
    ctx.arc(270, ey, 5, 0, Math.PI * 2);
    ctx.fill();

    // Vertical error bar
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(270, ey - 25);
    ctx.lineTo(270, ey + 25);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(265, ey - 25);
    ctx.lineTo(275, ey - 25);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(265, ey + 25);
    ctx.lineTo(275, ey + 25);
    ctx.stroke();

    // Horizontal error bar
    ctx.strokeStyle = "#EF4444";
    ctx.beginPath();
    ctx.moveTo(270 - 30, ey);
    ctx.lineTo(270 + 30, ey);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(270 - 30, ey - 4);
    ctx.lineTo(270 - 30, ey + 4);
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(270 + 30, ey - 4);
    ctx.lineTo(270 + 30, ey + 4);
    ctx.stroke();

    ctx.fillStyle = "#3B82F6";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("σ_y (e.g., count error)", 310, ey - 10);
    ctx.fillStyle = "#EF4444";
    ctx.fillText("σ_x (e.g., timing error)", 310, ey + 8);

    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Always report: value ± stat ± sys  (e.g., m = 105.7 ± 0.3 ± 0.1 MeV/c²)",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-red-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 18.5 – Signal vs Background */
export const SignalBackground = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Signal vs Background Separation", W / 2, 24);

    const m = { left: 60, right: 30, top: 48, bottom: 48 };
    const pw = W - m.left - m.right,
      ph = H - m.top - m.bottom;

    // Axes
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(m.left, m.top);
    ctx.lineTo(m.left, m.top + ph);
    ctx.lineTo(m.left + pw, m.top + ph);
    ctx.stroke();

    // Background: exponential decay
    ctx.fillStyle = "#EF444420";
    ctx.beginPath();
    ctx.moveTo(m.left, m.top + ph);
    for (let px = 0; px <= pw; px++) {
      const x = px / pw;
      const bkg = 0.85 * Math.exp(-x * 3);
      ctx.lineTo(m.left + px, m.top + ph - bkg * ph);
    }
    ctx.lineTo(m.left + pw, m.top + ph);
    ctx.closePath();
    ctx.fill();

    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let px = 0; px <= pw; px++) {
      const x = px / pw;
      const bkg = 0.85 * Math.exp(-x * 3);
      if (px === 0) ctx.moveTo(m.left + px, m.top + ph - bkg * ph);
      else ctx.lineTo(m.left + px, m.top + ph - bkg * ph);
    }
    ctx.stroke();

    // Signal: Gaussian bump
    const sigMu = 0.45,
      sigSigma = 0.06;
    ctx.fillStyle = "#22C55E30";
    ctx.beginPath();
    ctx.moveTo(m.left, m.top + ph);
    for (let px = 0; px <= pw; px++) {
      const x = px / pw;
      const sig =
        0.5 * Math.exp(-0.5 * Math.pow((x - sigMu) / sigSigma, 2));
      const bkg = 0.85 * Math.exp(-x * 3);
      ctx.lineTo(m.left + px, m.top + ph - (sig + bkg) * ph);
    }
    ctx.lineTo(m.left + pw, m.top + ph);
    ctx.closePath();
    ctx.fill();

    // Signal+Background total curve
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let px = 0; px <= pw; px++) {
      const x = px / pw;
      const sig =
        0.5 * Math.exp(-0.5 * Math.pow((x - sigMu) / sigSigma, 2));
      const bkg = 0.85 * Math.exp(-x * 3);
      if (px === 0) ctx.moveTo(m.left + px, m.top + ph - (sig + bkg) * ph);
      else ctx.lineTo(m.left + px, m.top + ph - (sig + bkg) * ph);
    }
    ctx.stroke();

    // Arrow pointing to signal peak
    const peakX = m.left + sigMu * pw;
    const peakBkg = 0.85 * Math.exp(-sigMu * 3);
    const peakSig = 0.5;
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(peakX + 50, m.top + 20);
    ctx.lineTo(peakX + 5, m.top + ph - (peakSig + peakBkg) * ph + 5);
    ctx.stroke();
    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Signal peak", peakX + 55, m.top + 25);

    // Legend
    ctx.fillStyle = "#EF444440";
    ctx.fillRect(380, 60, 12, 10);
    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Background", 398, 69);
    ctx.fillStyle = "#22C55E40";
    ctx.fillRect(380, 76, 12, 10);
    ctx.fillStyle = "#475569";
    ctx.fillText("Signal", 398, 85);

    // Axis labels
    ctx.fillStyle = "#475569";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Invariant mass (GeV/c²)", m.left + pw / 2, H - 6);

    // S/B ratio text
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText(
      "S/√(S+B) = significance  -  how 'visible' is the signal above noise?",
      W / 2,
      H - 22
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-green-50 to-red-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 18.6 – ROOT and Python Tools */
export const ROOTPython = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Analysis Tools: ROOT and Python", W / 2, 24);

    // ROOT side
    ctx.fillStyle = "#3B82F610";
    ctx.beginPath();
    ctx.roundRect(20, 44, 240, 170, 10);
    ctx.fill();
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(20, 44, 240, 170, 10);
    ctx.stroke();

    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 14px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("ROOT (CERN)", 140, 66);

    const rootFeatures = [
      "C++ framework (+ Python via PyROOT)",
      "TH1, TH2  -  histogram classes",
      "TF1  -  function fitting",
      "TTree  -  event-by-event data",
      "TCanvas  -  publication plots",
      "Standard in HEP for 25+ years",
    ];
    rootFeatures.forEach((f, i) => {
      ctx.fillStyle = "#3B82F6";
      ctx.font = "9px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("•", 35, 86 + i * 18);
      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      ctx.fillText(f, 45, 86 + i * 18);
    });

    // Python side
    ctx.fillStyle = "#22C55E10";
    ctx.beginPath();
    ctx.roundRect(280, 44, 240, 170, 10);
    ctx.fill();
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(280, 44, 240, 170, 10);
    ctx.stroke();

    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 14px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Python Ecosystem", 400, 66);

    const pyFeatures = [
      "NumPy  -  array math & operations",
      "Matplotlib  -  plotting & visualization",
      "SciPy  -  fitting, stats, optimization",
      "Pandas  -  tabular data handling",
      "Jupyter  -  interactive notebooks",
      "Easy to learn, great for BL4S!",
    ];
    pyFeatures.forEach((f, i) => {
      ctx.fillStyle = "#22C55E";
      ctx.font = "9px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("•", 295, 86 + i * 18);
      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      ctx.fillText(f, 305, 86 + i * 18);
    });

    // Common workflow
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath();
    ctx.roundRect(30, H - 38, 480, 28, 6);
    ctx.fill();
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Workflow: Collect data → Load into ROOT/Python → Histogram → Fit → Extract physics",
      W / 2,
      H - 18
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-green-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 18.7 – BL4S TOF Data Analysis (case study) */
export const BL4STOFAnalysis = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("BL4S TOF Data Analysis Pipeline", W / 2, 24);

    // Pipeline steps
    const steps = [
      { title: "Raw Data", desc: "TDC timestamps\nfrom S2 & S3", color: "#94A3B8" },
      { title: "Calibrate", desc: "Subtract offsets\nconvert to ns", color: "#3B82F6" },
      { title: "Histogram", desc: "bin TOF values\n→ distribution", color: "#22C55E" },
      { title: "Fit Peaks", desc: "Gaussian per\nparticle species", color: "#F59E0B" },
      { title: "Extract", desc: "Mass from\nTOF + known p", color: "#EF4444" },
    ];

    steps.forEach((s, i) => {
      const x = 18 + i * 107,
        y = 48,
        w = 96,
        h = 72;
      ctx.fillStyle = s.color + "10";
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.fill();
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(x, y, w, h, 8);
      ctx.stroke();
      ctx.fillStyle = s.color;
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(s.title, x + w / 2, y + 16);
      ctx.fillStyle = "#475569";
      ctx.font = "9px Inter,sans-serif";
      s.desc
        .split("\n")
        .forEach((l, li) =>
          ctx.fillText(l, x + w / 2, y + 32 + li * 12)
        );

      // Arrow
      if (i < steps.length - 1) {
        ctx.strokeStyle = "#94A3B8";
        ctx.lineWidth = 1.5;
        ctx.beginPath();
        ctx.moveTo(x + w + 2, y + h / 2);
        ctx.lineTo(x + w + 9, y + h / 2);
        ctx.stroke();
        ctx.fillStyle = "#94A3B8";
        ctx.beginPath();
        ctx.moveTo(x + w + 10, y + h / 2);
        ctx.lineTo(x + w + 6, y + h / 2 - 3);
        ctx.lineTo(x + w + 6, y + h / 2 + 3);
        ctx.closePath();
        ctx.fill();
      }
    });

    // Example result: 3-peak histogram
    const hy = 140,
      hh = 90,
      hw = 480;
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(40, hy);
    ctx.lineTo(40, hy + hh);
    ctx.lineTo(40 + hw, hy + hh);
    ctx.stroke();

    // Three Gaussian peaks
    const peaks = [
      { mu: 0.25, sigma: 0.035, amp: 0.9, label: "π", color: "#22C55E" },
      { mu: 0.50, sigma: 0.035, amp: 0.4, label: "K", color: "#F59E0B" },
      { mu: 0.72, sigma: 0.035, amp: 0.25, label: "p", color: "#3B82F6" },
    ];

    peaks.forEach((pk) => {
      ctx.fillStyle = pk.color + "30";
      ctx.beginPath();
      ctx.moveTo(40, hy + hh);
      for (let px = 0; px <= hw; px++) {
        const x = px / hw;
        const g = pk.amp * Math.exp(-0.5 * Math.pow((x - pk.mu) / pk.sigma, 2));
        ctx.lineTo(40 + px, hy + hh - g * hh);
      }
      ctx.lineTo(40 + hw, hy + hh);
      ctx.closePath();
      ctx.fill();

      ctx.strokeStyle = pk.color;
      ctx.lineWidth = 2;
      ctx.beginPath();
      for (let px = 0; px <= hw; px++) {
        const x = px / hw;
        const g = pk.amp * Math.exp(-0.5 * Math.pow((x - pk.mu) / pk.sigma, 2));
        if (px === 0) ctx.moveTo(40 + px, hy + hh - g * hh);
        else ctx.lineTo(40 + px, hy + hh - g * hh);
      }
      ctx.stroke();

      // Label
      ctx.fillStyle = pk.color;
      ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(pk.label, 40 + pk.mu * hw, hy + hh - pk.amp * hh - 8);
    });

    ctx.fillStyle = "#475569";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("TOF (ns)", 40 + hw / 2, hy + hh + 16);

    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText(
      "m = (p/c) · √((c·Δt/L)² − 1)  →  identify π, K, p from TOF peaks",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-amber-50 to-green-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

// ============================================
// Chapter 19  -  The Muon
// ============================================

/** 19.1 – Muon Discovery */
export const MuonDiscovery = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Discovery of the Muon", W / 2, 24);

    // Timeline
    const milestones = [
      { year: "1936", event: "Anderson & Neddermeyer\ndiscover muon in\ncosmic rays", color: "#3B82F6" },
      { year: "1937", event: "Street & Stevenson\nconfirm with cloud\nchamber", color: "#22C55E" },
      { year: "1947", event: "Distinction from pion\nclarified (π→μ decay\nchain)", color: "#F59E0B" },
      { year: "1962", event: "Lederman et al. prove\nμ-neutrino ≠ e-neutrino\n(Nobel 1988)", color: "#8B5CF6" },
    ];

    const ty = 60;
    // Timeline line
    ctx.strokeStyle = "#E2E8F0";
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.moveTo(60, ty);
    ctx.lineTo(480, ty);
    ctx.stroke();

    milestones.forEach((m2, i) => {
      const x = 80 + i * 110;
      ctx.fillStyle = m2.color;
      ctx.beginPath();
      ctx.arc(x, ty, 8, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = "white";
      ctx.font = "bold 8px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText((i + 1).toString(), x, ty + 3);

      ctx.fillStyle = m2.color;
      ctx.font = "bold 12px Inter,sans-serif";
      ctx.fillText(m2.year, x, ty - 18);

      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      m2.event
        .split("\n")
        .forEach((l, li) => ctx.fillText(l, x, ty + 26 + li * 13));
    });

    // Rabi quote
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath();
    ctx.roundRect(60, 150, 420, 40, 8);
    ctx.fill();
    ctx.fillStyle = "#92400E";
    ctx.font = "italic 13px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText('"Who ordered that?"  -  I. I. Rabi', W / 2, 175);

    // Properties summary
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 13px Inter,sans-serif";
    ctx.fillText("μ⁻: mass = 105.7 MeV/c²  |  charge = −1e  |  spin = ½  |  τ = 2.2 μs", W / 2, 215);
    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.fillText(
      "The muon is a heavier copy of the electron  -  a 2nd-generation lepton",
      W / 2,
      240
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-blue-50 to-amber-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 19.2 – Muon Properties Card */
export const MuonProperties = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Muon Properties", W / 2, 24);

    // Big particle card
    ctx.fillStyle = "#8B5CF620";
    ctx.beginPath();
    ctx.roundRect(40, 42, 200, 200, 14);
    ctx.fill();
    ctx.strokeStyle = "#8B5CF6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(40, 42, 200, 200, 14);
    ctx.stroke();

    ctx.fillStyle = "#8B5CF6";
    ctx.font = "bold 48px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("μ⁻", 140, 100);

    const props = [
      { label: "Mass", value: "105.658 MeV/c²" },
      { label: "Charge", value: "−1e" },
      { label: "Spin", value: "½" },
      { label: "Lifetime", value: "2.197 μs" },
      { label: "Generation", value: "2nd" },
      { label: "Type", value: "Lepton" },
    ];
    props.forEach((p, i) => {
      const y = 118 + i * 20;
      ctx.fillStyle = "#8B5CF6";
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "right";
      ctx.fillText(p.label, 110, y);
      ctx.fillStyle = "#1E293B";
      ctx.font = "10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(p.value, 118, y);
    });

    // Comparison table
    const rx = 270;
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Comparison with Electron & Tau", rx + 120, 58);

    const headers2 = ["", "e⁻", "μ⁻", "τ⁻"];
    headers2.forEach((h, i) => {
      ctx.fillStyle = "#1E293B";
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.fillText(h, rx + i * 65, 78);
    });

    const rows = [
      ["Mass", "0.511 MeV", "105.7 MeV", "1777 MeV"],
      ["Lifetime", "stable", "2.2 μs", "2.9�-10⁻¹³ s"],
      ["Ratio m/mₑ", "1", "207", "3477"],
      ["Main decay", " - ", "e + ν + ν̄", "π/K/e/μ + ν"],
    ];
    rows.forEach((row, ri) => {
      const y = 94 + ri * 26;
      ctx.fillStyle = ri % 2 === 0 ? "#F8FAFC" : "white";
      ctx.fillRect(rx - 20, y, 270, 24);
      row.forEach((val, ci) => {
        ctx.fillStyle = ci === 0 ? "#475569" : "#1E293B";
        ctx.font = ci === 0 ? "bold 10px Inter,sans-serif" : "10px Inter,sans-serif";
        ctx.textAlign = "center";
        ctx.fillText(val, rx + ci * 65, y + 16);
      });
    });

    // Key interactions
    ctx.fillStyle = "#EFF6FF";
    ctx.beginPath();
    ctx.roundRect(270, 200, 250, 42, 8);
    ctx.fill();
    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Interacts via: EM + Weak", 395, 218);
    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("Does NOT feel strong force → penetrating", 395, 234);

    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "Muons are MIPs in detectors  -  minimum-ionizing particles",
      W / 2,
      H - 8
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 19.3 – Cosmic Ray Muons */
export const CosmicRayMuons = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Cosmic Ray Muons", W / 2, 24);

    // Atmosphere layers
    const layers = [
      { label: "Top of atmosphere ~100 km", y: 50, color: "#1E3A5F" },
      { label: "Primary cosmic ray (p, He)", y: 65, color: "#EF4444" },
      { label: "Interaction ~15 km", y: 90, color: "#F59E0B" },
    ];

    // Dark sky
    ctx.fillStyle = "#0F172A";
    ctx.fillRect(20, 45, 500, 50);
    // Atmosphere gradient
    const grad = ctx.createLinearGradient(0, 95, 0, 200);
    grad.addColorStop(0, "#1E40AF30");
    grad.addColorStop(1, "#93C5FD10");
    ctx.fillStyle = grad;
    ctx.fillRect(20, 95, 500, 105);

    // Ground
    ctx.fillStyle = "#86EFAC30";
    ctx.fillRect(20, 200, 500, 30);
    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Ground level (sea level)", 270, 218);

    // Primary cosmic ray
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(270, 45);
    ctx.lineTo(270, 90);
    ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("Primary p", 280, 60);

    // Interaction shower (simplified)
    ctx.fillStyle = "#F59E0B";
    ctx.beginPath();
    ctx.arc(270, 90, 5, 0, Math.PI * 2);
    ctx.fill();
    ctx.font = "8px Inter,sans-serif";
    ctx.fillText("p + N → π, K, ...", 280, 93);

    // Pion/kaon to muon
    const muonPaths = [
      { startX: 270, endX: 200 },
      { startX: 270, endX: 270 },
      { startX: 270, endX: 340 },
    ];
    muonPaths.forEach((mp) => {
      // Pion leg
      ctx.strokeStyle = "#F59E0B";
      ctx.lineWidth = 1;
      ctx.setLineDash([3, 2]);
      ctx.beginPath();
      ctx.moveTo(270, 95);
      ctx.lineTo(mp.endX, 130);
      ctx.stroke();
      ctx.setLineDash([]);

      // π→μ decay
      ctx.fillStyle = "#F59E0B";
      ctx.beginPath();
      ctx.arc(mp.endX, 130, 3, 0, Math.PI * 2);
      ctx.fill();

      // Muon leg reaching ground
      ctx.strokeStyle = "#8B5CF6";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(mp.endX, 133);
      ctx.lineTo(mp.endX, 200);
      ctx.stroke();

      ctx.fillStyle = "#8B5CF6";
      ctx.font = "bold 9px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("μ", mp.endX, 145);
    });

    // Muon flux info
    ctx.fillStyle = "#EFF6FF";
    ctx.beginPath();
    ctx.roundRect(30, 232, 480, 38, 8);
    ctx.fill();
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "~10,000 muons/m²/min at sea level  |  ⟨E⟩ ≈ 4 GeV  |  nearly vertical",
      W / 2,
      248
    );
    ctx.fillStyle = "#64748B";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText(
      "They reach the ground due to relativistic time dilation (γτ >> τ)",
      W / 2,
      264
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-slate-50 to-purple-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 19.4 – Muon Time Dilation Demo */
export const MuonTimeDilationDemo = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Muon Survival via Time Dilation", W / 2, 24);

    // Two scenarios side-by-side
    const scenarios = [
      {
        title: "Without Relativity",
        subtitle: "(classical prediction)",
        dist: "cτ = 660 m",
        result: "Almost none reach ground!",
        fraction: "~0.0001%",
        barH: 0.03,
        color: "#EF4444",
      },
      {
        title: "With Relativity",
        subtitle: "(time dilation γ ≈ 30)",
        dist: "γcτ ≈ 20 km",
        result: "Many muons detected!",
        fraction: "~50%",
        barH: 0.5,
        color: "#22C55E",
      },
    ];

    scenarios.forEach((s, i) => {
      const ox = 30 + i * 280,
        w = 240;
      ctx.fillStyle = s.color + "08";
      ctx.beginPath();
      ctx.roundRect(ox, 45, w, 185, 10);
      ctx.fill();
      ctx.strokeStyle = s.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(ox, 45, w, 185, 10);
      ctx.stroke();

      ctx.fillStyle = s.color;
      ctx.font = "bold 12px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(s.title, ox + w / 2, 65);
      ctx.fillStyle = "#64748B";
      ctx.font = "10px Inter,sans-serif";
      ctx.fillText(s.subtitle, ox + w / 2, 80);

      // Vertical distance bar
      const barX = ox + 30,
        barTop = 95,
        barBot = 200;
      ctx.fillStyle = "#E2E8F0";
      ctx.fillRect(barX, barTop, 20, barBot - barTop);

      // Muon survival fraction
      const survH = s.barH * (barBot - barTop);
      ctx.fillStyle = s.color + "40";
      ctx.fillRect(barX, barBot - survH, 20, survH);

      ctx.fillStyle = "#475569";
      ctx.font = "9px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText("15 km", barX + 25, barTop + 5);
      ctx.fillText("0 km", barX + 25, barBot + 5);

      // Stats
      ctx.fillStyle = s.color;
      ctx.font = "bold 14px Inter,sans-serif";
      ctx.textAlign = "center";
      ctx.fillText(s.fraction, ox + w / 2 + 30, 140);
      ctx.fillStyle = "#475569";
      ctx.font = "10px Inter,sans-serif";
      ctx.fillText("reach sea level", ox + w / 2 + 30, 155);
      ctx.fillText("Decay length: " + s.dist, ox + w / 2 + 30, 172);
      ctx.fillStyle = s.color;
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.fillText(s.result, ox + w / 2, 218);
    });

    // Key formula
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 14px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "t_lab = γτ = τ/√(1−v²/c²)     γ = E/(mc²) ≈ 30 for 3 GeV muon",
      W / 2,
      252
    );
    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.fillText(
      "This is direct experimental proof of special relativity!",
      W / 2,
      272
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-red-50 to-green-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 19.5 – Muon g-2 Anomaly */
export const MuonG2Anomaly = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("The Muon g−2 Anomaly", W / 2, 24);

    // g-factor explanation
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 14px Inter,sans-serif";
    ctx.fillText("aμ = (g−2)/2     Anomalous magnetic moment", W / 2, 52);

    // Storage ring diagram
    const rcx = 150,
      rcy = 145,
      rr = 65;
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 12;
    ctx.beginPath();
    ctx.arc(rcx, rcy, rr, 0, Math.PI * 2);
    ctx.stroke();

    // Muons orbiting
    ctx.fillStyle = "#8B5CF6";
    for (let i = 0; i < 6; i++) {
      const a = (i / 6) * Math.PI * 2;
      ctx.beginPath();
      ctx.arc(rcx + Math.cos(a) * rr, rcy + Math.sin(a) * rr, 4, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Storage ring", rcx, rcy - 5);
    ctx.fillStyle = "#8B5CF6";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("μ⁺ (3.1 GeV/c)", rcx, rcy + 10);

    // Right: measurement vs theory
    const rx = 260;
    const measurements = [
      {
        label: "Experiment (BNL + FNAL)",
        value: "aμ = 116 592 061(41) �- 10⁻¹¹",
        color: "#EF4444",
      },
      {
        label: "SM Theory (2020 WP)",
        value: "aμ = 116 591 810(43) �- 10⁻¹¹",
        color: "#3B82F6",
      },
      {
        label: "Difference",
        value: "Δaμ = 251(59) �- 10⁻¹¹  (4.2σ!)",
        color: "#F59E0B",
      },
    ];

    measurements.forEach((m2, i) => {
      const y = 80 + i * 48;
      ctx.fillStyle = m2.color + "10";
      ctx.beginPath();
      ctx.roundRect(rx, y, 275, 40, 6);
      ctx.fill();
      ctx.strokeStyle = m2.color;
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.roundRect(rx, y, 275, 40, 6);
      ctx.stroke();
      ctx.fillStyle = m2.color;
      ctx.font = "bold 10px Inter,sans-serif";
      ctx.textAlign = "left";
      ctx.fillText(m2.label, rx + 8, y + 16);
      ctx.fillStyle = "#1E293B";
      ctx.font = "10px Inter,sans-serif";
      ctx.fillText(m2.value, rx + 8, y + 32);
    });

    // Why it matters
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath();
    ctx.roundRect(30, H - 38, 500, 28, 6);
    ctx.fill();
    ctx.fillStyle = "#92400E";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText(
      "If confirmed → hints at new physics beyond the Standard Model!",
      W / 2,
      H - 18
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-amber-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 19.6 – Muon Decay (Weak Interaction) */
export const MuonDecayWeak = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 540,
      H = 260;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Muon Decay via the Weak Force", W / 2, 24);

    // Feynman-like diagram
    const fcx = 200,
      fcy = 120;

    // Incoming muon
    ctx.strokeStyle = "#8B5CF6";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(60, fcy);
    ctx.lineTo(fcx, fcy);
    ctx.stroke();
    ctx.fillStyle = "#8B5CF6";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("μ⁻", 60, fcy - 12);

    // W boson (wavy line)
    ctx.strokeStyle = "#EF4444";
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let i = 0; i <= 60; i++) {
      const x = fcx + i;
      const y = fcy + Math.sin(i * 0.5) * 8;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    }
    ctx.stroke();
    ctx.fillStyle = "#EF4444";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("W⁻", fcx + 30, fcy + 25);

    // Outgoing neutrino (up-left)
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 2;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(fcx, fcy);
    ctx.lineTo(fcx - 60, fcy - 60);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("ν_μ", fcx - 70, fcy - 60);

    // Outgoing electron
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.moveTo(fcx + 60, fcy);
    ctx.lineTo(fcx + 140, fcy - 50);
    ctx.stroke();
    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("e⁻", fcx + 150, fcy - 50);

    // Outgoing anti-neutrino
    ctx.strokeStyle = "#F59E0B";
    ctx.setLineDash([4, 3]);
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(fcx + 60, fcy);
    ctx.lineTo(fcx + 140, fcy + 50);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#F59E0B";
    ctx.font = "bold 12px Inter,sans-serif";
    ctx.fillText("ν̄_e", fcx + 150, fcy + 55);

    // Vertex dots
    ctx.fillStyle = "#1E293B";
    ctx.beginPath();
    ctx.arc(fcx, fcy, 4, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.arc(fcx + 60, fcy, 4, 0, Math.PI * 2);
    ctx.fill();

    // Decay equation
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 16px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("μ⁻ → e⁻ + ν̄_e + ν_μ", W / 2, 200);

    // Key numbers
    ctx.fillStyle = "#475569";
    ctx.font = "12px Inter,sans-serif";
    ctx.fillText("τ = 2.197 μs  |  BR ≈ 100%  |  purely leptonic", W / 2, 225);
    ctx.fillStyle = "#64748B";
    ctx.font = "11px Inter,sans-serif";
    ctx.fillText(
      "Lepton number is conserved: L_μ and L_e each balanced",
      W / 2,
      248
    );
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-blue-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={540}
        height={260}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};

/** 19.7 – Cosmic Muon Experiment (case study) */
export const CosmicMuonExperiment = () => {
  const c = useRef(null);
  useEffect(() => {
    const W = 560,
      H = 280;
    const ctx = setupCanvas(c.current, W, H);
    ctx.fillStyle = "#FAFAFA";
    ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = "#1E293B";
    ctx.font = "bold 15px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Cosmic Muon Lifetime Experiment", W / 2, 24);

    // Setup diagram
    const dy = 110;

    // Scintillator 1
    ctx.fillStyle = "#22C55E30";
    ctx.fillRect(120, dy - 50, 150, 15);
    ctx.strokeStyle = "#22C55E";
    ctx.lineWidth = 2;
    ctx.strokeRect(120, dy - 50, 150, 15);
    ctx.fillStyle = "#22C55E";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Scintillator 1 (start)", 195, dy - 58);

    // Absorber block
    ctx.fillStyle = "#94A3B820";
    ctx.fillRect(120, dy - 30, 150, 60);
    ctx.strokeStyle = "#94A3B8";
    ctx.lineWidth = 2;
    ctx.strokeRect(120, dy - 30, 150, 60);
    ctx.fillStyle = "#64748B";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.fillText("Iron absorber", 195, dy);
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("(muon stops)", 195, dy + 14);

    // Scintillator 2
    ctx.fillStyle = "#3B82F630";
    ctx.fillRect(120, dy + 35, 150, 15);
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.strokeRect(120, dy + 35, 150, 15);
    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.fillText("Scintillator 2 (decay e⁻)", 195, dy + 62);

    // Cosmic muon arrow
    ctx.strokeStyle = "#8B5CF6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(195, dy - 80);
    ctx.lineTo(195, dy - 52);
    ctx.stroke();
    ctx.fillStyle = "#8B5CF6";
    ctx.beginPath();
    ctx.moveTo(195, dy - 50);
    ctx.lineTo(191, dy - 56);
    ctx.lineTo(199, dy - 56);
    ctx.closePath();
    ctx.fill();
    ctx.font = "bold 10px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("cosmic μ", 200, dy - 75);

    // Decay electron
    ctx.strokeStyle = "#EF4444";
    ctx.setLineDash([3, 2]);
    ctx.beginPath();
    ctx.moveTo(195, dy + 10);
    ctx.lineTo(195, dy + 35);
    ctx.stroke();
    ctx.setLineDash([]);
    ctx.fillStyle = "#EF4444";
    ctx.font = "9px Inter,sans-serif";
    ctx.textAlign = "left";
    ctx.fillText("e⁻", 200, dy + 28);

    // Right side: exponential decay fit
    const rx = 330,
      rw = 200,
      rh = 120;
    ctx.strokeStyle = "#1E293B";
    ctx.lineWidth = 1.5;
    ctx.beginPath();
    ctx.moveTo(rx, 60);
    ctx.lineTo(rx, 60 + rh);
    ctx.lineTo(rx + rw, 60 + rh);
    ctx.stroke();

    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Δt (μs)", rx + rw / 2, 60 + rh + 16);

    // Exponential decay curve
    ctx.strokeStyle = "#3B82F6";
    ctx.lineWidth = 2;
    ctx.beginPath();
    for (let px = 0; px <= rw; px++) {
      const t = (px / rw) * 10; // 0-10 μs
      const n = Math.exp(-t / 2.2);
      const y = 60 + rh - n * rh * 0.95;
      if (px === 0) ctx.moveTo(rx + px, y);
      else ctx.lineTo(rx + px, y);
    }
    ctx.stroke();

    // Scatter data
    ctx.fillStyle = "#1E293B";
    for (let i = 0; i < 15; i++) {
      const t = 0.5 + i * 0.6;
      const n = Math.exp(-t / 2.2) + (Math.random() - 0.5) * 0.08;
      const x = rx + (t / 10) * rw;
      const y = 60 + rh - Math.max(0, n) * rh * 0.95;
      ctx.beginPath();
      ctx.arc(x, y, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.fillStyle = "#3B82F6";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("N(t) = N₀ · e^(-t/τ)", rx + rw / 2, 60 + rh + 32);

    // Results
    ctx.fillStyle = "#FEF3C7";
    ctx.beginPath();
    ctx.roundRect(310, 215, 230, 55, 8);
    ctx.fill();
    ctx.fillStyle = "#92400E";
    ctx.font = "bold 11px Inter,sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Measured: τ ≈ 2.1 ± 0.2 μs", 425, 235);
    ctx.fillStyle = "#475569";
    ctx.font = "10px Inter,sans-serif";
    ctx.fillText("PDG value: τ = 2.197 μs", 425, 252);
    ctx.fillText("Agreement within errors!", 425, 265);
  }, []);
  return (
    <div className="my-8 p-6 bg-gradient-to-br from-purple-50 to-amber-50 rounded-2xl border border-slate-200">
      <canvas
        ref={c}
        width={560}
        height={280}
        className="mx-auto block rounded-xl bg-white shadow-inner"
        style={{ maxWidth: "100%" }}
      />
    </div>
  );
};
